import { dayjsNow, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { ParsedDate } from '@api/Trinium/types';
import { Dayjs } from 'dayjs';

import styles from './RenderCell.module.css';

interface DateSlotProps {
	/** Beginning of time range, if only one date provided from date takes precendence over to date */
	fromDate?: ParsedDate;
	/** Ending of time range */
	toDate?: ParsedDate;
	/** Flag indicating not to display the time */
	dateOnly?: boolean;
	/** Flag indicating to display time in title */
	showTimeInTitle?: boolean;
	/** Flag indicating to use the full date format MM/DD/YYYY */
	fullDate?: boolean;
	/** Flag indicating to highlight the cell when the date is past today */
	usePastDueStyle?: boolean;
}

const startingTimeOfDay = '00:00';
const endingTimeOfDay = '23:59';

/**
 * Component used for rendering a Datetime range as a RenderCell slot.
 */
export default function DateSlot({ fromDate, toDate, ...props }: DateSlotProps) {
	const currentTime = dayjsNow();

	// If we have no date range, it cannot be past due.
	let pastDueCheck = false;
	const fromDayjs = parseDayJs(fromDate?.unix);
	const toDayjs = parseDayJs(toDate?.unix);

	if ((fromDayjs?.isValid() && toDayjs?.isValid() && toDayjs.isAfter(fromDayjs)) || (!fromDayjs?.isValid() && toDayjs?.isValid())) {
		// If the time is exactly midnight, consider the entire day
		pastDueCheck = currentTime.isAfter(isMidnight(toDayjs) ? toDayjs.endOf('day') : toDayjs);
	} else if (fromDayjs?.isValid()) {
		// If the time is exactly midnight, consider the entire day as long as there is no specified To Time IE 00:00 - 9:00
		pastDueCheck = currentTime.isAfter(
			isMidnight(fromDayjs) && (!toDayjs?.isValid() || isMidnight(toDayjs)) ? fromDayjs.endOf('day') : fromDayjs
		);
	}

	// If we weren't given a date, don't render anything
	if (!fromDate && !toDate) {
		return <></>;
	}

	const date = fromDate ? fromDate : toDate;

	const timeDisplay = () => {
		if (fromDate?.time && toDate?.time && toDate.time !== startingTimeOfDay) {
			// If it's representing the entire day, don't display
			return fromDate.time === startingTimeOfDay && toDate.time === endingTimeOfDay ? '' : `${fromDate.time} - ${toDate.time}`;
		}

		if (fromDate?.time === startingTimeOfDay) return '';

		// Otherwise return whichever one we have
		return fromDate?.time ?? toDate?.time;
	};

	return (
		<div className={`${styles['date-slot']} ${pastDueCheck && props.usePastDueStyle ? styles['past-due'] : ''}`}>
			<span
				className={`${styles['ellipsis']} ${styles['bold']}`}
				title={`${date?.date ?? ''}${props.showTimeInTitle ? ` ${timeDisplay() ?? ''}` : ''}`}
			>
				{props.fullDate ? date?.date : date?.shortDate}
			</span>
			{!props.dateOnly && <span className={styles['time-range']}>{timeDisplay()}</span>}
		</div>
	);
}

function isMidnight(dayjsDate: Dayjs) {
	return dayjsDate.hour() === 0 && dayjsDate.minute() === 0 && dayjsDate.second() === 0;
}
